/** @jsxImportSource ./simboluri-jsx */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DrawingData, DrawingEntity, DrawingPoly, ZonaInteres } from "./tipuri";
import { fetchDrawingUrl, fileDrawingUrl, updateFileZone } from "./apiStructura";
import { useRolProiect } from "../components/rol";
import { listFiles } from "../api";
import { t, tn, useT, type Cheie } from "../i18n";
import { displayColor, displayFill } from "./culoriDesen";

// Generic vector-drawing viewer. DrawingView only knows the DrawingData JSON
// shape and a URL that serves it, so anything the platform produces in that
// shape (imported DWGs, step previews, generated proposals) gets the same
// layer panel / pan / zoom / inspect UX. DwgViewer is the route wrapper that
// resolves a project file name to its drawing URL.

interface View {
  scale: number; // world units -> screen px
  cx: number;    // world point at canvas centre
  cy: number;
}

// Hârtie caldă, în loc de alb pur (planșa `ViewerDwg`): ACI 7 / culorile
// aproape albe se răstoarnă în cerneala paletei, ca desenele făcute pentru un
// canvas negru să rămână citibile.
const BG = "#f5f6f8";
// SELECȚIA e accentul platformei, nu albastrul de AutoCAD (planșa ViewerDwg
// desenează evidențierea cu #0b7f92). Textul din legenda Pasului 6 spune
// „cărămiziu" — v. `.st-legenda-selectie`.
const SEL_STROKE = "#0b7f92";       // --acc
const SEL_FILL = "rgba(15, 127, 124,0.28)";
const HOVER_STROKE = "rgba(15, 127, 124,0.70)";
const HOVER_FILL = "rgba(15, 127, 124,0.10)";
const ZONA_LUCRU = "#b8730c";       // conturul în lucru / mânerul apucat
const ZONA_FIXARE = "#4f8207";      // punctul „lipit" de desen (snap) — --ok

// ---- reperele plutitoare (roza cardinală + axele clădirii) ---------------
// Aceleași tokenuri ca interfața (app.css): reperele desenate pe canvas
// trebuie să arate ca niște carduri ale platformei, nu ca widget-uri de CAD
//. Geometria stă tot aici, ca cele două discuri să nu se
// mai poată suprapune: unul se așază SUB celălalt, din razele lor.
const FONT_UI = "'Instrument Sans', 'IBM Plex Sans', system-ui, sans-serif";
const CUL_ACC = "#0b7f92";          // --acc
const CUL_MUT = "#737373";          // --mut
const CUL_LINE2 = "#c8c8c8";        // --line2
const CUL_AXA_X = "#8792a2";        // gri de oțel: axele nu trebuie să semene cu grinzile
const CUL_AXA_Y = "#8792a2";        // același gri pe ambele direcții
const R_ROZA = 22;                  // raza rozei cardinale
const Y_ROZA = 14 + R_ROZA;         // centrul ei, la 14 px de marginea de sus
// raza VIZUALĂ a indicatorului de axe: săgeata are 17 px, eticheta stă la
// 17 + 8 de centru, plus 5 px de aer până la cerc
const R_AXE = 17 + 8 + 5;

/** discul semi-transparent pe care stau reperele — cardul platformei, desenat
 *  în spațiul ecranului (nu se scalează cu zoom-ul). */
function discPlutitor(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, 2 * Math.PI);
  ctx.save();
  ctx.shadowColor = "rgba(30, 36, 36,0.10)";
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = "rgba(255,255,255,0.88)";
  ctx.fill();
  ctx.restore();
  ctx.strokeStyle = CUL_LINE2;
  ctx.lineWidth = 1;
  ctx.stroke();
}

type Pt = [number, number];

// O zonă de interes e un POLIGON. Zonele salvate înainte (dreptunghiuri, fără
// `pts`) se citesc ca poligonul celor patru colțuri, ca tot restul codului —
// desenare, mânere, decupare — să știe de o singură formă.
function zonaPts(z: ZonaInteres): Pt[] {
  if (z.pts && z.pts.length >= 3) return z.pts.map((p) => [p[0], p[1]] as Pt);
  return [[z.minx, z.miny], [z.maxx, z.miny], [z.maxx, z.maxy], [z.minx, z.maxy]];
}

// poligonul -> forma salvată: punctele + anvelopa (o ține și serverul, ca
// filtrele grosiere să n-o recalculeze la fiecare citire a desenului)
function ptsZona(pts: Pt[]): ZonaInteres {
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const [x, y] of pts) {
    if (x < minx) minx = x;
    if (x > maxx) maxx = x;
    if (y < miny) miny = y;
    if (y > maxy) maxy = y;
  }
  return { pts: pts.map((p) => [p[0], p[1]] as Pt), minx, miny, maxx, maxy };
}

// mânerul de sub cursor: vârful `i` al zonei `z`, ori latura care pleacă din el
type Mana = { z: number; tip: "v" | "e"; i: number };

const ringsOf = (e: DrawingPoly) => (e.holes ? [e.pts, ...e.holes] : [e.pts]);

/** Mărimea pe ecran a unui text a cărui înălțime adevărată ar fi `brut`
 *  pixeli: `null` sub `TEXT_ASCUNS_PX` (nu se desenează), altfel cel puțin
 *  `TEXT_MINIM_PX`, ca să rămână citibil. Un text mărit mai mult de o
 *  treime față de locul lui se calcă pe desenul de lângă el — pe coala
 *  întreagă a plăcii marca «c5» de 5 px, urcată la 8, stătea peste capra de
 *  montaj, iar titlul notei ieșea din chenar —, de aceea pragul de ascundere
 *  e la 6 px: ce se mărește, se mărește cel mult de 8/6. Textele mici
 *  reapar la mărimea lor la primul zoom. */
export const TEXT_ASCUNS_PX = 6;
export const TEXT_MINIM_PX = 8;
export function marimeaTextului(brut: number): number | null {
  if (!(brut >= TEXT_ASCUNS_PX)) return null;
  return Math.max(brut, TEXT_MINIM_PX);
}

/** Tiparul liniei întrerupte al unei entități (`ld`, în unitățile
 *  desenului), în pixeli de ecran la scara `scale`; `null` când entitatea e
 *  continuă sau când liniuțele ar ieși sub 2 px — atunci linia se desenează
 *  continuă, altfel ar fi un praf de puncte. Punctele tiparului (lungime 0)
 *  rămân vizibile prin lungimea minimă de un pixel. */
export function tiparulLiniei(ld: number[] | undefined, scale: number): number[] | null {
  if (!ld || ld.length < 2) return null;
  const dash = ld.map((v) => Math.max(v * scale, 1));
  if (dash[0] < 2) return null;
  return dash;
}

// Route wrapper: the platform addresses a drawing by the FILE UUID
// (`/p/:pid/dossier/dwg/:fileId`), not by name. The
// file row (name + saved zones) comes from the platform files API; the
// drawing JSON comes through the structure facade.
export default function DwgViewer({
  projectId,
  fileId,
}: {
  projectId: string;
  fileId: string;
}) {
  useT();
  const [error, setError] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>("");
  const [gata, setGata] = useState(false);
  const [zona, setZona] = useState<ZonaInteres[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setGata(false);
    setZona(null);
    listFiles(projectId)
      .then((files) => {
        if (cancelled) return;
        const file = files.find((f) => f.id === fileId);
        if (!file) throw new Error(t("struct.dwg.fisierul_nu_exista"));
        setFilename(file.name);
        const z = (file.zone ?? []) as ZonaInteres[];
        setZona(z.length ? z : null);
        setGata(true);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, fileId]);

  // Zonele de interes se SCRIU pe rândul fișierului, deci cer drept de
  // scriere; fără el, viewerul rămâne un vizualizator.
  const { potScrie } = useRolProiect();

  // saving/clearing the zones of interest — persists on the file row
  // (core.file.drawing->'zone'); the analysis steps see only the union of
  // the zones (the cropping happens server-side, when the drawing is read)
  const onZonaChange = useCallback(
    async (z: ZonaInteres[] | null) => {
      const r = (await updateFileZone(projectId, fileId, z)) as {
        drawing?: { zone?: ZonaInteres[] };
      };
      const salvat = r?.drawing?.zone ?? null;
      setZona(salvat && salvat.length ? salvat : null);
    },
    [projectId, fileId]
  );

  if (error) {
    return (
      <div className="dv-page">
        <div className="dv-toolbar">
          <a className="sg-btnlink" href={`/p/${projectId}/dossier`}>{t("struct.dwg.inapoi_dosar")}</a>
          <strong className="dv-filename">{filename}</strong>
        </div>
        <div className="dv-center"><div className="sg-error">{error}</div></div>
      </div>
    );
  }
  return (
    <DrawingView
      src={gata ? fileDrawingUrl(projectId, fileId) : null}
      title={filename}
      backHash={`/p/${projectId}/dossier`}
      zona={zona}
      onZonaChange={potScrie ? onZonaChange : undefined}
    />
  );
}

/** Grupa și numele de meniu ale unui strat al planului de structură. Straturile
 *  vin cu numele scrise de server («Stâlpi propuși (30×30)», ««document»
 *  pereți»); aici se decide doar dacă sunt ale structurii sau ale arhitecturii
 *  de dedesubt și se scoate prefixul de proveniență. */
export function grupaStratului(nume: string): { grupa: "structura" | "arhitectura"; nume: string } {
  const curat = nume.replace(/^«[^»]*»\s*/u, "").trim();
  const frumos = curat ? curat[0].toLocaleUpperCase("ro-RO") + curat.slice(1) : nume;
  const structura = /st[aâ]lp|grind|persisten|simplifica|lamel|fundat|talp|t[aă]lp|esv|beton/i
    .test(curat);
  return { grupa: structura ? "structura" : "arhitectura", nume: frumos };
}

export function DrawingView({
  src,
  versiune,
  title,
  backHash,
  embed,
  focusId,
  onSelect,
  onJump,
  linkEticheta,
  zona,
  onZonaChange,
  straturiIn,
  faraAntet,
  faraBusola,
}: {
  src: string | null;
  // MENIUL STUDIOULUI: straturile desenului se scriu în grupul «Afișare» al
  // meniului din stânga, ca în 3D, iar coloana lor de 208 px dispare de pe
  // pânză. Fără el, coloana rămâne (paginile pașilor, fișierele).
  straturiIn?: HTMLElement | null;
  /** fără bara de sus (titlul, entitățile, unitățile, «Încadrează»): pentru
   *  desenele puse într-un card care are deja titlul și butoanele lui */
  faraAntet?: boolean;
  /** fără roza cardinală și indicatorul axelor: o planșă de armare nu e
   *  georeferențiată, deci nordul ei n-ar însemna nimic */
  faraBusola?: boolean;
  /** versiunea desenului (`updated_at` al pasului), cheia memoriei din
   *  `fetchDrawingUrl`; fără ea desenul se cere de fiecare dată */
  versiune?: string | null;
  title: string;
  backHash?: string;
  embed?: boolean;
  // id-ul entității care trebuie selectată și centrată din exterior (jump-to
  // dinspre tabele/indicator); se schimbă → desenul o aduce în vizor
  focusId?: string | null;
  // click pe o entitate din desen → id-ul ei, ca pagina să poată selecta
  // același element în tabele
  onSelect?: (id: string | null, entity: DrawingEntity | null) => void;
  // navigarea către tabel: se declanșează DOAR din butonul panoului de
  // informații, niciodată din clickul pe desen. Cine se uită la desen vrea să
  // rămână la desen — saltul e o acțiune cerută explicit.
  onJump?: (id: string | null, entity: DrawingEntity | null) => void;
  // eticheta butonului de „du-mă în tabel" din panoul de informații
  linkEticheta?: string;
  // zonele de interes salvate pe fișier (complex imobiliar: se analizează un
  // singur corp de pe planșă; pot fi mai multe dreptunghiuri — fațada +
  // scara ei de cote) — desenate cu chenar + voal peste restul desenului
  zona?: ZonaInteres[] | null;
  // prezent DOAR pe ruta fișierului (nu în preview-urile pașilor): activează
  // butoanele de selectare/salvare/ștergere a zonelor
  onZonaChange?: (z: ZonaInteres[] | null) => Promise<void>;
}) {
  useT();
  const [status, setStatus] = useState(t("struct.dwg.se_incarca"));
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DrawingData | null>(null);
  const [visible, setVisible] = useState<boolean[]>([]);
  const [layerFilter, setLayerFilter] = useState("");
  const [selected, setSelected] = useState<DrawingEntity | null>(null);
  // zonele de interes: modul de editare (click = punct, click pe primul punct
  // = închide conturul, apoi vârfurile și laturile se trag cu mouse-ul),
  // poligoanele de lucru (copiile celor salvate + cele noi), conturul aflat
  // în curs de trasare, și starea salvării
  const [zonaSel, setZonaSel] = useState(false);
  const [zonaLucru, setZonaLucru] = useState<Pt[][]>([]);
  const [zonaCurent, setZonaCurent] = useState<Pt[]>([]);
  const [zonaBusy, setZonaBusy] = useState(false);
  const [zonaErr, setZonaErr] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<View>({ scale: 1, cx: 0, cy: 0 });
  const visibleRef = useRef<boolean[]>([]);
  const selectedRef = useRef<DrawingEntity | null>(null);
  const hoverRef = useRef<DrawingEntity | null>(null);
  const rafRef = useRef(0);
  // stratul voalului zonelor de interes (reciclat între cadre)
  const veilRef = useRef<HTMLCanvasElement | null>(null);
  // callback-ul de selecție se ține în ref, ca handlerii de mouse să nu se
  // rebindeze la fiecare render al paginii-părinte
  const onSelectRef = useRef(onSelect);

  // zonele se citesc din ref-uri în draw()/handlerii de mouse, ca ei să nu se
  // rebindeze la fiecare render (același tipar ca visibleRef)
  const zonaRef = useRef<ZonaInteres[]>([]);
  const zonaLucruRef = useRef<Pt[][]>([]);
  const zonaCurentRef = useRef<Pt[]>([]);
  const zonaSelRef = useRef(false);
  // mânerul de sub cursor, poziția cursorului (deja „lipită" de desen, dacă
  // s-a găsit o fixare) și dacă e chiar o fixare — toate doar pentru desen
  const zonaManaRef = useRef<Mana | null>(null);
  const zonaCursorRef = useRef<Pt | null>(null);
  const zonaFixatRef = useRef(false);

  visibleRef.current = visible;
  selectedRef.current = selected;
  onSelectRef.current = onSelect;
  zonaRef.current = zona ?? [];
  zonaSelRef.current = zonaSel;
  // poligoanele de lucru se sincronizează în efect, nu la render: tragerea
  // unui vârf mută punctele DIRECT în ref (fără re-render la fiecare cadru)
  // și le comite în state abia la ridicarea butonului — o re-randare a
  // paginii-părinte în timpul tragerii ar arunca punctele înapoi
  useEffect(() => { zonaLucruRef.current = zonaLucru; }, [zonaLucru]);
  useEffect(() => { zonaCurentRef.current = zonaCurent; }, [zonaCurent]);

  // ---- data loading -------------------------------------------------------

  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    setData(null);
    setError(null);
    setStatus(t("struct.dwg.se_interpreteaza"));
    fetchDrawingUrl(src, versiune)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setVisible(d.layers.map((l) => l.on));
        setSelected(null);
        hoverRef.current = null;
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [src, versiune]);

  // ---- rendering ----------------------------------------------------------

  // per-entity world bounds, computed once per drawing (draw culling + picking)
  const bboxes = useMemo(() => {
    if (!data) return new Float64Array(0);
    const n = data.entities.length;
    const bb = new Float64Array(n * 4);
    for (let i = 0; i < n; i++) {
      const e = data.entities[i];
      let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
      if (e.t === "p") {
        for (const ring of ringsOf(e)) {
          for (const [x, y] of ring) {
            if (x < minx) minx = x;
            if (x > maxx) maxx = x;
            if (y < miny) miny = y;
            if (y > maxy) maxy = y;
          }
        }
      } else {
        // texts extend past their insert point — pad by a rough glyph extent
        const pad = e.t === "t" ? e.h * (1 + 0.6 * Math.min(e.s.length, 24)) : 0;
        minx = e.x - pad; maxx = e.x + pad;
        miny = e.y - pad; maxy = e.y + pad;
      }
      bb[i * 4] = minx; bb[i * 4 + 1] = miny; bb[i * 4 + 2] = maxx; bb[i * 4 + 3] = maxy;
    }
    return bb;
  }, [data]);

  // Încadrarea „utilă": extents-urile DWG-urilor reale sunt deseori umflate
  // de sute de ori de blocuri uitate departe de planșe (pe Olteniței: un LIFT
  // inserat cu scara -1000, hidranți cu scara ×1180, axe la 500 km) — la fit
  // pe extents desenul devine 2 pixeli și pagina pare goală. AutoCAD maschează
  // problema fiindcă salvează ultima vedere în fișier; noi n-o avem. Cutia
  // robustă: gardul mediană ± 3·(p95−p5) pe centrele entităților, apoi
  // extinsă la bbox-urile complete ale entităților păstrate. Pe planurile
  // Olteniței păstrează 96–97% din entități și strânge aria de ~30 000×.
  const fitBounds = useMemo(() => {
    if (!data) return null;
    const n = data.entities.length;
    if (n < 50) return null; // desen mic — extents-urile sunt de încredere
    const cxs = new Float64Array(n);
    const cys = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      cxs[i] = (bboxes[i * 4] + bboxes[i * 4 + 2]) / 2;
      cys[i] = (bboxes[i * 4 + 1] + bboxes[i * 4 + 3]) / 2;
    }
    // o trecere de tundere: gardul mediană ± 3·lățime pe centrele entităților
    // rămase, cutia = bbox-urile complete ale celor păstrate, TĂIATĂ la gard
    // (altfel o singură linie kilometrică păstrată re-umflă tot). Lățimea
    // vine de la apelant: p95−p5 la prima trecere, p75−p25 la a doua.
    const trim = (idx: number[], loP: number, hiP: number) => {
      const m = idx.length;
      const sx = Float64Array.from(idx, (i) => cxs[i]).sort();
      const sy = Float64Array.from(idx, (i) => cys[i]).sort();
      const pct = (s: Float64Array, p: number) => s[Math.min(m - 1, Math.floor((p / 100) * m))];
      const spanX = Math.max(pct(sx, hiP) - pct(sx, loP), 1e-9);
      const spanY = Math.max(pct(sy, hiP) - pct(sy, loP), 1e-9);
      const fxLo = pct(sx, 50) - 3 * spanX, fxHi = pct(sx, 50) + 3 * spanX;
      const fyLo = pct(sy, 50) - 3 * spanY, fyHi = pct(sy, 50) + 3 * spanY;
      const kept: number[] = [];
      let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
      for (const i of idx) {
        if (cxs[i] < fxLo || cxs[i] > fxHi || cys[i] < fyLo || cys[i] > fyHi) continue;
        kept.push(i);
        if (bboxes[i * 4] < minx) minx = bboxes[i * 4];
        if (bboxes[i * 4 + 1] < miny) miny = bboxes[i * 4 + 1];
        if (bboxes[i * 4 + 2] > maxx) maxx = bboxes[i * 4 + 2];
        if (bboxes[i * 4 + 3] > maxy) maxy = bboxes[i * 4 + 3];
      }
      if (!kept.length || !Number.isFinite(minx)) return null;
      return {
        kept,
        box: {
          minx: Math.max(minx, fxLo), miny: Math.max(miny, fyLo),
          maxx: Math.min(maxx, fxHi), maxy: Math.min(maxy, fyHi),
        },
      };
    };

    const all = Array.from({ length: n }, (_, i) => i);
    // trecerea 1 scoate rătăciții de departe; trecerea 2 strânge pe mulțimea
    // dominantă (planșa cu desenul), lăsând legendele/copiile împrăștiate pe
    // lângă — cu plasa că fiecare treaptă trebuie să păstreze ≥60% din TOATE
    // entitățile, altfel rămâne cutia treptei dinainte.
    const t1 = trim(all, 5, 95);
    if (!t1 || t1.kept.length < n * 0.6) return null;
    let box = t1.box;
    const t2 = trim(t1.kept, 25, 75);
    if (t2 && t2.kept.length >= n * 0.6) box = t2.box;
    if (box.maxx <= box.minx || box.maxy <= box.miny) return null;
    const b = data.bounds;
    const full = (b.maxx - b.minx) * (b.maxy - b.miny);
    // câștig neglijabil — nu merită o a doua noțiune de „încadrare"
    if (full > 0 && ((box.maxx - box.minx) * (box.maxy - box.miny)) / full > 0.6) return null;
    return box;
  }, [data, bboxes]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    const { scale, cx, cy } = viewRef.current;
    const sx = (x: number) => (x - cx) * scale + W / 2;
    const sy = (y: number) => (cy - y) * scale + H / 2;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);
    ctx.lineWidth = 1;
    ctx.lineJoin = "round";

    const vis = visibleRef.current;
    const sel = selectedRef.current;
    const hov = hoverRef.current;
    const ents = data.entities;
    const bb = bboxes;
    // view culling bounds in world coords, slightly padded
    const pad = 20 / scale;
    const wxMin = cx - W / 2 / scale - pad;
    const wxMax = cx + W / 2 / scale + pad;
    const wyMin = cy - H / 2 / scale - pad;
    const wyMax = cy + H / 2 / scale + pad;
    const culled = (i: number) =>
      bb[i * 4 + 2] < wxMin || bb[i * 4] > wxMax ||
      bb[i * 4 + 3] < wyMin || bb[i * 4 + 1] > wyMax;

    const tracePoly = (e: DrawingPoly) => {
      ctx.beginPath();
      for (const ring of ringsOf(e)) {
        if (ring.length < 2) continue;
        ctx.moveTo(sx(ring[0][0]), sy(ring[0][1]));
        for (let i = 1; i < ring.length; i++) ctx.lineTo(sx(ring[i][0]), sy(ring[i][1]));
        if (e.closed) ctx.closePath();
      }
    };
    const strokeSegs = (segs: [number, number, number, number][], color: string, width: number) => {
      ctx.beginPath();
      for (const s of segs) {
        ctx.moveTo(sx(s[0]), sy(s[1]));
        ctx.lineTo(sx(s[2]), sy(s[3]));
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineCap = "round"; // zero-length segments (dot patterns) render as dots
      ctx.stroke();
      ctx.lineCap = "butt";
    };
    const drawText = (e: Extract<DrawingEntity, { t: "t" }>, color: string) => {
      const px = marimeaTextului(e.h * scale);
      if (px === null) return;
      ctx.save();
      ctx.translate(sx(e.x), sy(e.y));
      ctx.rotate((-e.r * Math.PI) / 180);
      ctx.fillStyle = color;
      ctx.font = `${px}px "IBM Plex Sans", sans-serif`;
      // attachment point 1..9: 1=TL 2=TC 3=TR / 4=ML.. / 7=BL 8=BC 9=BR
      const col = (e.a - 1) % 3;
      const rowA = Math.floor((e.a - 1) / 3); // 0 top, 1 middle, 2 bottom
      ctx.textAlign = col === 0 ? "left" : col === 1 ? "center" : "right";
      const lines = e.s.split("\n");
      const lh = px * 1.25;
      for (let i = 0; i < lines.length; i++) {
        let y: number;
        if (rowA === 0) y = i * lh + px;               // top-anchored
        else if (rowA === 1) y = (i - (lines.length - 1) / 2) * lh + px * 0.35;
        else y = (i - (lines.length - 1)) * lh;        // bottom-anchored
        ctx.fillText(lines[i], 0, y);
      }
      ctx.restore();
    };
    const drawCross = (e: Extract<DrawingEntity, { t: "n" }>, color: string) => {
      const x = sx(e.x), y = sy(e.y);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - 3, y); ctx.lineTo(x + 3, y);
      ctx.moveTo(x, y - 3); ctx.lineTo(x, y + 3);
      ctx.stroke();
    };

    // pass 1 — filled areas (solid hatches, SOLID, generated volumes) go
    // under all linework, like AutoCAD's draw order for solids
    for (let i = 0; i < ents.length; i++) {
      const e = ents[i];
      if (e.t !== "p" || !e.fill || !vis[e.l] || culled(i)) continue;
      tracePoly(e);
      ctx.fillStyle = e.et === "HATCH" ? displayFill(e.fill) : displayColor(e.fill);
      ctx.fill("evenodd");
    }

    // pass 2 — linework, hatch patterns, text, points
    for (let i = 0; i < ents.length; i++) {
      const e = ents[i];
      if (!vis[e.l]) continue;
      if (e.t === "p") {
        if (culled(i)) continue;
        const color = displayColor(e.c);
        if (e.segs) {
          // hatch pattern lines; when denser than the screen area they cover,
          // a translucent tint reads better (and draws faster)
          const bw = (bb[i * 4 + 2] - bb[i * 4]) * scale;
          const bh = (bb[i * 4 + 3] - bb[i * 4 + 1]) * scale;
          if (e.segs.length * 3 > bw * bh) {
            tracePoly(e);
            ctx.fillStyle = color + "26";
            ctx.fill("evenodd");
          } else {
            strokeSegs(e.segs, color, 1);
          }
          continue; // hatches have no boundary stroke of their own
        }
        if (e.et === "HATCH") continue; // solid/tinted fill painted in pass 1
        tracePoly(e);
        ctx.strokeStyle = color;
        ctx.lineWidth = e.w
          ? Math.min(4, Math.max(1, e.w * 0.05))
          : e.et === "OUTLINE" || e.et === "FOUNDATION" ? 2 : 1;
        const dash = tiparulLiniei(e.ld, scale);
        if (dash) ctx.setLineDash(dash);
        ctx.stroke();
        if (dash) ctx.setLineDash([]);
      } else if (e.t === "t") {
        if (culled(i)) continue;
        drawText(e, displayColor(e.c));
      } else {
        if (culled(i)) continue;
        drawCross(e, displayColor(e.c));
      }
    }

    // pass 3 — hover + selection overlays on top (Autodesk-style blue)
    const overlay = (e: DrawingEntity, stroke: string, fill: string) => {
      if (e.t === "p") {
        tracePoly(e);
        if (e.closed) {
          ctx.fillStyle = fill;
          ctx.fill("evenodd");
        }
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 2;
        ctx.stroke();
        if (e.segs) strokeSegs(e.segs, stroke, 1.2);
      } else if (e.t === "t") {
        drawText(e, stroke);
      } else {
        drawCross(e, stroke);
      }
    };
    if (hov && hov !== sel && vis[hov.l]) overlay(hov, HOVER_STROKE, HOVER_FILL);
    if (sel && vis[sel.l]) overlay(sel, SEL_STROKE, SEL_FILL);

    // pass 3.5 — zonele de interes: voal alb peste tot ce rămâne PE DINAFARĂ
    // reuniunii lor (exact ce nu vor vedea pașii de analiză) + conturul
    // fiecărui poligon. În modul de editare se desenează poligoanele de lucru
    // (copiile celor salvate + cele noi), cu mânere pe vârfuri, plus conturul
    // aflat în trasare — portocaliu, cu banda elastică până la cursor.
    {
      const inMod = zonaSelRef.current;
      const inele: Pt[][] = inMod
        ? zonaLucruRef.current
        : zonaRef.current.map(zonaPts);
      const curent = inMod ? zonaCurentRef.current : [];
      const cursor = inMod ? zonaCursorRef.current : null;
      const mana = inMod ? zonaManaRef.current : null;

      const traseu = (c: CanvasRenderingContext2D, pts: Pt[], inchis: boolean) => {
        c.beginPath();
        c.moveTo(sx(pts[0][0]), sy(pts[0][1]));
        for (let i = 1; i < pts.length; i++) c.lineTo(sx(pts[i][0]), sy(pts[i][1]));
        if (inchis) c.closePath();
      };
      const maner = (p: Pt, r: number, umplere: string, contur: string) => {
        const x = sx(p[0]), y = sy(p[1]);
        ctx.fillStyle = umplere;
        ctx.strokeStyle = contur;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.rect(x - r, y - r, 2 * r, 2 * r);
        ctx.fill();
        ctx.stroke();
      };

      const cuArie = inele.filter((p) => p.length >= 3);
      if (cuArie.length) {
        // voalul se construiește pe un strat separat: se umple tot, apoi se
        // ȘTERGE fiecare poligon (destination-out) — zonele care se suprapun
        // nu se anulează reciproc, cum ar face un fill even-odd
        const dpr = window.devicePixelRatio || 1;
        let veil = veilRef.current;
        if (!veil) veil = veilRef.current = document.createElement("canvas");
        if (veil.width !== canvas.width || veil.height !== canvas.height) {
          veil.width = canvas.width;
          veil.height = canvas.height;
        }
        const vc = veil.getContext("2d");
        if (vc) {
          vc.setTransform(dpr, 0, 0, dpr, 0, 0);
          vc.clearRect(0, 0, W, H);
          vc.fillStyle = "rgba(255,255,255,0.75)";
          vc.fillRect(0, 0, W, H);
          vc.globalCompositeOperation = "destination-out";
          for (const pts of cuArie) {
            traseu(vc, pts, true);
            vc.fill();
          }
          vc.globalCompositeOperation = "source-over";
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.drawImage(veil, 0, 0);
          ctx.restore();
        }
      }

      // conturele închise
      ctx.setLineDash([7, 5]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = SEL_STROKE;
      for (const pts of inele) {
        if (pts.length < 2) continue;
        traseu(ctx, pts, true);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      if (inMod) {
        // latura de sub cursor, îngroșată: se poate apuca și trage întreagă
        if (mana && mana.tip === "e" && inele[mana.z]) {
          const pts = inele[mana.z];
          const a = pts[mana.i], b = pts[(mana.i + 1) % pts.length];
          ctx.strokeStyle = ZONA_LUCRU;
          ctx.lineWidth = 3.5;
          ctx.beginPath();
          ctx.moveTo(sx(a[0]), sy(a[1]));
          ctx.lineTo(sx(b[0]), sy(b[1]));
          ctx.stroke();
        }
        for (let z = 0; z < inele.length; z++) {
          for (let i = 0; i < inele[z].length; i++) {
            const activ = !!mana && mana.z === z && mana.tip === "v" && mana.i === i;
            maner(inele[z][i], activ ? 5 : 3.5,
                  activ ? ZONA_LUCRU : "#ffffff",
                  activ ? ZONA_LUCRU : SEL_STROKE);
          }
        }
      }

      // conturul în curs de trasare: linia pusă + banda elastică spre cursor
      // și coarda punctată de închidere înapoi la primul punct
      if (curent.length) {
        ctx.strokeStyle = ZONA_LUCRU;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(sx(curent[0][0]), sy(curent[0][1]));
        for (let i = 1; i < curent.length; i++) {
          ctx.lineTo(sx(curent[i][0]), sy(curent[i][1]));
        }
        if (cursor) ctx.lineTo(sx(cursor[0]), sy(cursor[1]));
        ctx.stroke();
        if (cursor && curent.length >= 2) {
          ctx.setLineDash([5, 4]);
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(sx(cursor[0]), sy(cursor[1]));
          ctx.lineTo(sx(curent[0][0]), sy(curent[0][1]));
          ctx.stroke();
          ctx.setLineDash([]);
        }
        for (let i = 0; i < curent.length; i++) {
          maner(curent[i], 3.5, "#ffffff", ZONA_LUCRU);
        }
        // primul punct, ținta clickului de închidere
        if (curent.length >= 3) {
          ctx.strokeStyle = ZONA_LUCRU;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(sx(curent[0][0]), sy(curent[0][1]), 9, 0, 2 * Math.PI);
          ctx.stroke();
        }
      }

      // punctul „lipit" de desen: pătrat verde, ca omul să vadă CE colț de
      // perete va prinde clickul următor
      if (inMod && cursor && zonaFixatRef.current) {
        const x = sx(cursor[0]), y = sy(cursor[1]);
        ctx.strokeStyle = ZONA_FIXARE;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.rect(x - 6, y - 6, 12, 12);
        ctx.moveTo(x - 9, y); ctx.lineTo(x + 9, y);
        ctx.moveTo(x, y - 9); ctx.lineTo(x, y + 9);
        ctx.stroke();
      }
    }

    // Nordul desenului: stanțat de server (`nord_grade`, antiorar față de
    // +X, cu +Y în sus) când documentul e georeferențiat și desenul stă pe
    // alt cadru decât al planului — colile de planșe stau pe axele clădirii.
    // Fără el, pe previzualizările în cadrul planului, nordul e +Y (în sus).
    // O coală «fără busolă» primește totuși roza dacă are nordul pe ea: o
    // planșă rotită față de nord fără roză nu se poate așeza pe teren.
    const nordGrade = typeof data.nord_grade === "number" ? data.nord_grade : null;
    if (faraBusola && nordGrade == null) return;

    // pass 4 — roza punctelor cardinale. Se desenează în spațiul ECRANULUI (nu
    // se scalează cu zoom-ul), ultima, în colțul din dreapta-sus — zona care
    // de regulă nu acoperă desenul. Acul stă pe direcția nordului, iar
    // estul pe a lui (`est_grade`): pe un cadru oglindit față de hartă estul
    // ar cădea în stânga nordului, și roza o arată așa, nu ascunde.
    {
      const rz = R_ROZA;
      const rx = W - 14 - rz;
      const ry = Y_ROZA;
      const aN = ((nordGrade ?? 90) * Math.PI) / 180;
      const aE = ((typeof data.est_grade === "number" ? data.est_grade : (nordGrade ?? 90) - 90) * Math.PI) / 180;
      // +Y al desenului urcă pe ecran ⇒ pe canvas y se inversează
      const dir = (a: number, r: number): [number, number] => [rx + Math.cos(a) * r, ry - Math.sin(a) * r];
      ctx.save();
      discPlutitor(ctx, rx, ry, rz);
      // acul: jumătatea de nord plină, cărămiziu (--acc); coada de sud, --line2
      const [nx, ny] = dir(aN, 10);
      const [sx0, sy0] = dir(aN + Math.PI, 10);
      const [l1x, l1y] = dir(aN + Math.PI / 2, 3.5);
      const [l2x, l2y] = dir(aN - Math.PI / 2, 3.5);
      ctx.beginPath();
      ctx.moveTo(nx, ny);
      ctx.lineTo(l1x, l1y);
      ctx.lineTo(l2x, l2y);
      ctx.closePath();
      ctx.fillStyle = CUL_ACC;
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(sx0, sy0);
      ctx.lineTo(l1x, l1y);
      ctx.lineTo(l2x, l2y);
      ctx.closePath();
      ctx.fillStyle = CUL_LINE2;
      ctx.fill();
      // literele N / S / E / V (românește), cu N accentuat
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `bold 9px ${FONT_UI}`;
      ctx.fillStyle = CUL_ACC;
      const [tnx, tny] = dir(aN, rz - 7);
      ctx.fillText("N", tnx, tny);
      ctx.font = `8px ${FONT_UI}`;
      ctx.fillStyle = CUL_MUT;
      const [tsx, tsy] = dir(aN + Math.PI, rz - 7);
      const [tex, tey] = dir(aE, rz - 7);
      const [tvx, tvy] = dir(aE + Math.PI, rz - 7);
      ctx.fillText("S", tsx, tsy);
      ctx.fillText("E", tex, tey);
      ctx.fillText(t("struct.dwg.cardinal_vest"), tvx, tvy);
      ctx.restore();
    }
    if (faraBusola) return;

    // pass 5 — indicatorul axelor CLĂDIRII. Textele
    // pașilor vorbesc despre „direcția X" și „direcția Y" — ale clădirii, nu
    // ale desenului: planul e georeferențiat Stereo 70, deci grila poate fi
    // rotită față de nord. Fără un reper în desen, cele două nume nu se pot
    // lega de nimic. Unghiul vine din Pasul 2, aceeași valoare pe care o
    // folosesc Pașii 4 și 5, deci indicatorul arată exact axele după care
    // sunt clasificate axele de fundație și direcțiile de drift.
    const ac = data.axe_cardinale;
    if (ac && typeof ac.theta_grade === "number") {
      const th = (ac.theta_grade * Math.PI) / 180;
      const L = 17;
      const ox = W - 14 - R_AXE;
      // sub roza cardinală, cu aer între cele două discuri: eticheta „X" stă
      // la L + 8 px de centru, deci raza vizuală a indicatorului e R_AXE —
      // înainte cele două se atingeau („busola și xy se
      // suprapun")
      const oy = Y_ROZA + R_ROZA + 12 + R_AXE;
      // +Y al desenului urcă pe ecran ⇒ pe canvas y se inversează
      const dir = (a: number): [number, number] => [
        Math.cos(a) * L,
        -Math.sin(a) * L,
      ];
      const [xdx, xdy] = dir(th);
      const [ydx, ydy] = dir(th + Math.PI / 2);
      ctx.save();
      discPlutitor(ctx, ox, oy, R_AXE);
      const sageata = (dx: number, dy: number, culoare: string, et: string) => {
        ctx.strokeStyle = culoare;
        ctx.fillStyle = culoare;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(ox, oy);
        ctx.lineTo(ox + dx, oy + dy);
        ctx.stroke();
        // vârful
        const n = Math.hypot(dx, dy) || 1;
        const ux = dx / n;
        const uy = dy / n;
        ctx.beginPath();
        ctx.moveTo(ox + dx, oy + dy);
        ctx.lineTo(ox + dx - ux * 5 - uy * 3, oy + dy - uy * 5 + ux * 3);
        ctx.lineTo(ox + dx - ux * 5 + uy * 3, oy + dy - uy * 5 - ux * 3);
        ctx.closePath();
        ctx.fill();
        ctx.font = `bold 9px ${FONT_UI}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(et, ox + ux * (L + 8), oy + uy * (L + 8));
      };
      sageata(xdx, xdy, CUL_AXA_X, "X");
      sageata(ydx, ydy, CUL_AXA_Y, "Y");
      // numele cardinale, ca reperul să fie citibil și fără roza de deasupra
      ctx.font = `8px ${FONT_UI}`;
      ctx.fillStyle = CUL_MUT;
      ctx.textAlign = "right";
      ctx.textBaseline = "top";
      ctx.fillText(t("struct.dwg.axele_cladirii_x", { p1: ac.X, p2: ac.Y }), W - 14, oy + R_AXE + 8);
      ctx.restore();
    }
  }, [data, bboxes, faraBusola]);

  const requestDraw = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(draw);
  }, [draw]);

  const fit = useCallback((tot?: boolean) => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    const b = (!tot && fitBounds) || data.bounds;
    const bw = Math.max(b.maxx - b.minx, 1e-6);
    const bh = Math.max(b.maxy - b.miny, 1e-6);
    viewRef.current = {
      scale: Math.min(W / bw, H / bh) * 0.92,
      cx: (b.minx + b.maxx) / 2,
      cy: (b.miny + b.maxy) / 2,
    };
    requestDraw();
  }, [data, fitBounds, requestDraw]);

  // canvas sizing (HiDPI aware) + first fit
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas || !data) return;
    let first = true;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(wrap.clientWidth * dpr));
      canvas.height = Math.max(1, Math.round(wrap.clientHeight * dpr));
      canvas.style.width = `${wrap.clientWidth}px`;
      canvas.style.height = `${wrap.clientHeight}px`;
      // prima încadrare așteaptă ca wrap-ul să aibă mărimea REALĂ: la montare
      // clientWidth poate fi încă ~0 (layoutul nu s-a așezat), iar un fit pe
      // 1 px lasă o scară de mii de ori prea mică — desenul „dispare" și doar
      // click pe Încadrează îl aducea înapoi. ResizeObserver-ul revine oricum
      // cu mărimea finală, deci încadrarea se face la primul apel cu mărime
      // adevărată, nu la primul apel pur și simplu.
      if (first && wrap.clientWidth > 10 && wrap.clientHeight > 10) {
        first = false;
        fit();
      } else {
        requestDraw();
      }
    };
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();
    return () => ro.disconnect();
  }, [data, fit, requestDraw]);

  useEffect(requestDraw,
            [visible, selected, zona, zonaSel, zonaLucru, zonaCurent, requestDraw]);

  // cursorul cruciuliță cât timp se desenează zona de interes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) canvas.style.cursor = zonaSel ? "crosshair" : "grab";
  }, [zonaSel]);

  // închiderea conturului (click pe primul punct sau Enter): poligonul trece
  // în lista de lucru și se poate edita ca oricare altul
  const inchideZona = useCallback(() => {
    const c = zonaCurentRef.current;
    if (c.length < 3) return;
    zonaCurentRef.current = [];
    setZonaCurent([]);
    setZonaLucru((prev) => [...prev, c]);
  }, []);

  // tastele modului de zonă: Enter închide conturul, Backspace scoate ultimul
  // punct pus, Esc renunță la conturul început
  useEffect(() => {
    if (!zonaSel) return;
    const onKey = (ev: KeyboardEvent) => {
      const t = ev.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      if (ev.key === "Enter") inchideZona();
      else if (ev.key === "Escape") setZonaCurent([]);
      else if (ev.key === "Backspace" || ev.key === "Delete") {
        ev.preventDefault();
        setZonaCurent((prev) => prev.slice(0, -1));
      } else return;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zonaSel, inchideZona]);

  // jump-to din exterior: entitatea cu id-ul cerut se selectează, layerul ei
  // se aprinde (dacă era stins) și vederea se centrează pe ea fără să schimbe
  // zoomul — utilizatorul își păstrează scara la care se uita
  useEffect(() => {
    if (!data || !focusId) return;
    const i = data.entities.findIndex((e) => e.id === focusId && e.t === "p");
    const j = i >= 0 ? i : data.entities.findIndex((e) => e.id === focusId);
    if (j < 0) return;
    const e = data.entities[j];
    setSelected(e);
    setVisible((prev) => (prev[e.l] ? prev
      : prev.map((on, k) => (k === e.l ? true : on))));
    const cx = (bboxes[j * 4] + bboxes[j * 4 + 2]) / 2;
    const cy = (bboxes[j * 4 + 1] + bboxes[j * 4 + 3]) / 2;
    if (Number.isFinite(cx) && Number.isFinite(cy)) {
      viewRef.current.cx = cx;
      viewRef.current.cy = cy;
    }
    requestDraw();
  }, [data, bboxes, focusId, requestDraw]);

  // ---- interaction --------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;

    const toWorld = (ev: { clientX: number; clientY: number }) => {
      const r = canvas.getBoundingClientRect();
      const { scale, cx, cy } = viewRef.current;
      return {
        x: (ev.clientX - r.left - r.width / 2) / scale + cx,
        y: cy - (ev.clientY - r.top - r.height / 2) / scale,
      };
    };

    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const before = toWorld(ev);
      const factor = Math.exp(-ev.deltaY * 0.0015);
      viewRef.current.scale *= factor;
      const after = toWorld(ev);
      viewRef.current.cx += before.x - after.x;
      viewRef.current.cy += before.y - after.y;
      requestDraw();
    };

    let dragging = false;
    let moved = 0;
    let last = { x: 0, y: 0 };
    let hoverRaf = 0;
    // mânerul apucat (vârf sau latură), poziția din lume unde s-a apucat și
    // copia poligonului de atunci — laturile se mută cu tot cu ambele capete,
    // deci deplasarea se ia mereu față de forma dinaintea tragerii
    let apucat: Mana | null = null;
    let apucatDe: { x: number; y: number } | null = null;
    let apucatOrig: Pt[] | null = null;

    // FIXAREA pe desen (snap): punctul pus se lipește de vârful de geometrie
    // din apropiere, iar dacă nu e niciunul, de proiecția pe cea mai apropiată
    // linie. Fără ea, tăierea exact pe colțul unui perete e chestiune de
    // noroc la un pixel — iar tocmai precizia e rostul zonei. Shift o oprește.
    const fixeaza = (w: { x: number; y: number }, fara: boolean) => {
      if (fara) return { x: w.x, y: w.y, fix: false };
      const scale = viewRef.current.scale;
      const tolV = 12 / scale;   // vârfuri
      const tolL = 8 / scale;    // linii
      const ents = data.entities;
      const vis = visibleRef.current;
      let vx = 0, vy = 0, dv = tolV;
      let lx = 0, ly = 0, dl = tolL;
      for (let i = 0; i < ents.length; i++) {
        const e = ents[i];
        if (e.t !== "p" || !vis[e.l]) continue;
        const o = i * 4;
        if (w.x < bboxes[o] - tolV || w.x > bboxes[o + 2] + tolV ||
            w.y < bboxes[o + 1] - tolV || w.y > bboxes[o + 3] + tolV) continue;
        for (const ring of ringsOf(e)) {
          for (let j = 0; j < ring.length; j++) {
            const p = ring[j];
            const d = Math.hypot(w.x - p[0], w.y - p[1]);
            if (d < dv) { dv = d; vx = p[0]; vy = p[1]; }
            const q = j + 1 < ring.length ? ring[j + 1]
                    : (e.closed && ring.length > 2 ? ring[0] : null);
            if (q) {
              const f = segFoot(w.x, w.y, p, q);
              if (f.d < dl) { dl = f.d; lx = f.x; ly = f.y; }
            }
          }
        }
      }
      if (dv < tolV) return { x: vx, y: vy, fix: true };
      if (dl < tolL) return { x: lx, y: ly, fix: true };
      return { x: w.x, y: w.y, fix: false };
    };

    // mânerul de sub cursor: vârfurile bat laturile (altfel n-ai mai putea
    // apuca un vârf care stă chiar pe o latură vecină)
    const manaSub = (w: { x: number; y: number }): Mana | null => {
      const scale = viewRef.current.scale;
      const zone = zonaLucruRef.current;
      let best: Mana | null = null;
      let bd = 9 / scale;
      for (let z = 0; z < zone.length; z++) {
        const pts = zone[z];
        for (let i = 0; i < pts.length; i++) {
          const d = Math.hypot(w.x - pts[i][0], w.y - pts[i][1]);
          if (d < bd) { bd = d; best = { z, tip: "v", i }; }
        }
      }
      if (best) return best;
      bd = 6 / scale;
      for (let z = 0; z < zone.length; z++) {
        const pts = zone[z];
        if (pts.length < 2) continue;
        for (let i = 0; i < pts.length; i++) {
          const d = segDist(w.x, w.y, pts[i], pts[(i + 1) % pts.length]);
          if (d < bd) { bd = d; best = { z, tip: "e", i }; }
        }
      }
      return best;
    };

    // clickul din modul zonă: pe primul punct = închide conturul, altfel pune
    // un punct nou (fixat pe desen)
    const puncteaza = (ev: MouseEvent) => {
      const brut = toWorld(ev);
      const curent = zonaCurentRef.current;
      if (curent.length >= 3) {
        const tol = 10 / viewRef.current.scale;
        if (Math.hypot(brut.x - curent[0][0], brut.y - curent[0][1]) < tol) {
          inchideZona();
          return;
        }
      }
      const w = fixeaza(brut, ev.shiftKey);
      setZonaCurent([...curent, [w.x, w.y] as Pt]);
    };

    const comiteZone = () => {
      setZonaLucru(zonaLucruRef.current.map((p) => p.map((q) => [q[0], q[1]] as Pt)));
    };

    const onDown = (ev: MouseEvent) => {
      if (zonaSelRef.current && ev.button === 0) {
        // cât timp se trasează un contur, mânerele celorlalte zone nu se
        // apucă — orice click e un punct nou (sau închiderea)
        const m = zonaCurentRef.current.length ? null : manaSub(toWorld(ev));
        if (m) {
          if (ev.altKey && m.tip === "v") {
            // Alt+click pe un vârf îl scoate (sub 3 vârfuri n-ar mai fi zonă)
            setZonaLucru((prev) => prev.map((p, z) =>
              z === m.z && p.length > 3 ? p.filter((_, i) => i !== m.i) : p));
            return;
          }
          apucat = m;
          apucatDe = toWorld(ev);
          apucatOrig = zonaLucruRef.current[m.z].map((p) => [p[0], p[1]] as Pt);
          moved = 0;
          return;
        }
        // fără mâner sub cursor: tragerea mută planul, clickul simplu pune
        // un punct — pragul de 5 px le desparte (vezi onUp)
        dragging = true;
        moved = 0;
        last = { x: ev.clientX, y: ev.clientY };
        return;
      }
      dragging = true;
      moved = 0;
      last = { x: ev.clientX, y: ev.clientY };
    };
    const onMove = (ev: MouseEvent) => {
      if (apucat && apucatOrig && apucatDe) {
        const pts = zonaLucruRef.current[apucat.z];
        if (!pts) { apucat = null; return; }
        if (apucat.tip === "v") {
          const w = fixeaza(toWorld(ev), ev.shiftKey);
          pts[apucat.i] = [w.x, w.y];
        } else {
          // latura se mută întreagă, paralel cu ea însăși
          const w = toWorld(ev);
          const dx = w.x - apucatDe.x;
          const dy = w.y - apucatDe.y;
          const j = (apucat.i + 1) % pts.length;
          pts[apucat.i] = [apucatOrig[apucat.i][0] + dx, apucatOrig[apucat.i][1] + dy];
          pts[j] = [apucatOrig[j][0] + dx, apucatOrig[j][1] + dy];
        }
        moved += 1;
        requestDraw();
        return;
      }
      if (!dragging) return;
      const dx = ev.clientX - last.x;
      const dy = ev.clientY - last.y;
      moved += Math.abs(dx) + Math.abs(dy);
      last = { x: ev.clientX, y: ev.clientY };
      const { scale } = viewRef.current;
      viewRef.current.cx -= dx / scale;
      viewRef.current.cy += dy / scale;
      requestDraw();
    };
    const onUp = (ev: MouseEvent) => {
      if (apucat) {
        apucat = null;
        apucatDe = null;
        apucatOrig = null;
        comiteZone();
        return;
      }
      if (!dragging) return;
      dragging = false;
      if (zonaSelRef.current) {
        if (moved < 5 && ev.button === 0) puncteaza(ev);
        return;
      }
      if (moved < 5) {
        const w = toWorld(ev);
        const hit = pick(data, bboxes, visibleRef.current, w.x, w.y,
                         8 / viewRef.current.scale);
        setSelected(hit);
        onSelectRef.current?.(hit?.id ?? null, hit);
      }
    };

    // dublu-click pe o latură: adaugă un vârf acolo (îndesești conturul exact
    // unde ai nevoie de detaliu, fără să reiei zona)
    const onDbl = (ev: MouseEvent) => {
      if (!zonaSelRef.current || zonaCurentRef.current.length) return;
      const m = manaSub(toWorld(ev));
      if (!m || m.tip !== "e") return;
      const w = fixeaza(toWorld(ev), ev.shiftKey);
      setZonaLucru((prev) => prev.map((p, z) =>
        z === m.z ? [...p.slice(0, m.i + 1), [w.x, w.y] as Pt, ...p.slice(m.i + 1)] : p));
    };

    // hover pre-highlight, like Autodesk viewers (rAF-throttled hit test)
    const onHover = (ev: MouseEvent) => {
      if (zonaSelRef.current) {
        // în modul zonă hover-ul urmărește cursorul (banda elastică), fixarea
        // pe desen și mânerul de sub cursor
        if (hoverRaf || apucat || dragging) return;
        const pos = { clientX: ev.clientX, clientY: ev.clientY, shift: ev.shiftKey };
        hoverRaf = requestAnimationFrame(() => {
          hoverRaf = 0;
          const brut = toWorld(pos);
          const w = fixeaza(brut, pos.shift);
          zonaCursorRef.current = [w.x, w.y];
          zonaFixatRef.current = w.fix;
          zonaManaRef.current = zonaCurentRef.current.length ? null : manaSub(brut);
          canvas.style.cursor = zonaManaRef.current ? "move" : "crosshair";
          requestDraw();
        });
        return;
      }
      if (dragging || hoverRaf) return;
      const pos = { clientX: ev.clientX, clientY: ev.clientY };
      hoverRaf = requestAnimationFrame(() => {
        hoverRaf = 0;
        const w = toWorld(pos);
        const h = pick(data, bboxes, visibleRef.current, w.x, w.y, 8 / viewRef.current.scale);
        if (h !== hoverRef.current) {
          hoverRef.current = h;
          canvas.style.cursor = h ? "pointer" : "grab";
          requestDraw();
        }
      });
    };
    const onLeave = () => {
      if (zonaSelRef.current) {
        zonaCursorRef.current = null;
        zonaFixatRef.current = false;
        zonaManaRef.current = null;
        requestDraw();
        return;
      }
      if (hoverRef.current) {
        hoverRef.current = null;
        canvas.style.cursor = "grab";
        requestDraw();
      }
    };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("mousedown", onDown);
    canvas.addEventListener("dblclick", onDbl);
    canvas.addEventListener("mousemove", onHover);
    canvas.addEventListener("mouseleave", onLeave);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      cancelAnimationFrame(hoverRaf);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("mousedown", onDown);
      canvas.removeEventListener("dblclick", onDbl);
      canvas.removeEventListener("mousemove", onHover);
      canvas.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [data, bboxes, requestDraw, inchideZona]);

  // ---- layer panel --------------------------------------------------------

  const filteredLayers = useMemo(() => {
    if (!data) return [];
    const q = layerFilter.trim().toLowerCase();
    return data.layers
      .map((l, i) => ({ ...l, i }))
      .filter((l) => !q || l.name.toLowerCase().includes(q));
  }, [data, layerFilter]);

  const setAll = (on: boolean) => {
    if (!data) return;
    const next = visible.slice();
    for (const l of filteredLayers) next[l.i] = on;
    setVisible(next);
  };

  // ---- zonele de interes: salvare / ștergere ------------------------------

  // intrarea în mod ia zonele salvate ca poligoane de LUCRU: se pot muta
  // vârfuri și laturi pe ele, nu doar adăuga zone noi. Salvarea le scrie pe
  // toate (lista de lucru E noua stare), deci o zonă ștearsă aici dispare.
  const intraZona = () => {
    setZonaLucru((zona ?? []).map(zonaPts));
    setZonaCurent([]);
    setZonaSel(true);
    setSelected(null);
    setZonaErr(null);
  };

  const salveazaZona = async () => {
    if (!onZonaChange) return;
    setZonaBusy(true);
    setZonaErr(null);
    try {
      const zone = zonaLucru.filter((p) => p.length >= 3).map(ptsZona);
      await onZonaChange(zone.length ? zone : null);
      setZonaLucru([]);
      setZonaCurent([]);
      setZonaSel(false);
    } catch (e) {
      setZonaErr((e as Error).message);
    } finally {
      setZonaBusy(false);
    }
  };

  const stergeZona = async () => {
    if (!onZonaChange) return;
    if (!window.confirm(t("struct.dwg.confirma_sterge_zone"))) return;
    setZonaBusy(true);
    setZonaErr(null);
    try {
      await onZonaChange(null);
      setZonaLucru([]);
      setZonaCurent([]);
    } catch (e) {
      setZonaErr((e as Error).message);
    } finally {
      setZonaBusy(false);
    }
  };

  // ---- render -------------------------------------------------------------

  const pageClass = embed ? "dv-page dv-embed" : "dv-page";

  const header = faraAntet ? null : (
    <div className="dv-toolbar">
      {backHash && <a className="sg-btnlink" href={backHash}>{t("struct.dwg.inapoi_proiect")}</a>}
      <strong className="dv-filename">{title}</strong>
      {data && (
        <>
          {/* Câte entități și în ce unități: date de fișier, utile în dosar.
              Pe planul din vizualizator nu spun nimic structuristului. */}
          {!straturiIn && (
            <>
              <span className="sg-pill">
                {tn("struct.dwg.entitati#n", data.entities.length)}
              </span>
              <span className="sg-pill">{t("struct.dwg.unitati", { u: data.units })}</span>
            </>
          )}
          <span className="sg-spacer" />
          {onZonaChange && (
            zonaSel ? (
              <>
                <button className="sg-primary" disabled={zonaBusy} onClick={salveazaZona}>
                  {zonaBusy ? t("struct.dwg.se_salveaza")
                    : zonaLucru.length === 0 ? t("struct.dwg.salveaza_fara_zone")
                    : tn("struct.dwg.zone_salveaza#n", zonaLucru.length)}
                </button>
                {zonaCurent.length > 0 && (
                  <button
                    disabled={zonaBusy}
                    onClick={() => setZonaCurent((prev) => prev.slice(0, -1))}
                    title={t("struct.dwg.scoate_ultimul_punct")}
                  >
                    {t("struct.dwg.inapoi_punct")}
                  </button>
                )}
                {zonaCurent.length >= 3 && (
                  <button disabled={zonaBusy} onClick={inchideZona}
                          title={t("struct.dwg.inchide_conturul_enter")}>
                    {t("struct.dwg.inchide_conturul")}
                  </button>
                )}
                {zonaLucru.length > 0 && zonaCurent.length === 0 && (
                  <button
                    disabled={zonaBusy}
                    onClick={() => setZonaLucru((prev) => prev.slice(0, -1))}
                    title={t("struct.dwg.scoate_ultimul_poligon")}
                  >
                    {t("struct.dwg.sterge_ultima_zona")}
                  </button>
                )}
                <button
                  disabled={zonaBusy}
                  onClick={() => { setZonaSel(false); setZonaLucru([]); setZonaCurent([]); }}
                >
                  {t("struct.dwg.renunta")}
                </button>
              </>
            ) : (
              <>
                {zona && (
                  <span
                    className="sg-pill dv-zona-pill"
                    title={t("struct.dwg.pasii_analiza_vad")}
                  >
                    ✓ {tn("struct.dwg.zone_active#n", zona.length)}
                  </span>
                )}
                <button
                  disabled={zonaBusy}
                  onClick={intraZona}
                  title={t("struct.dwg.complex_imobiliar_alege")}
                >
                  {t(zona ? "struct.dwg.editeaza_zonele"
                          : "struct.dwg.selecteaza_zone")}
                </button>
                {zona && (
                  <button className="sg-danger" disabled={zonaBusy} onClick={stergeZona}>
                    {t("struct.dwg.sterge_zonele")}
                  </button>
                )}
              </>
            )
          )}
          <button onClick={() => fit()}>{t("struct.dwg.incadreaza")}</button>
          {fitBounds && (
            <button
              onClick={() => fit(true)}
              title={t("struct.dwg.incadreaza_extents_urile")}
            >
              {t("struct.dwg.tot_desenul")}
            </button>
          )}
        </>
      )}
    </div>
  );

  if (error) {
    return (
      <div className={pageClass}>
        {header}
        <div className="dv-center"><div className="sg-error">{error}</div></div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className={pageClass}>
        {header}
        <div className="dv-center"><div className="sg-muted">{status}</div></div>
      </div>
    );
  }

  const visCount = visible.filter(Boolean).length;
  // Straturile planului din vizualizator, GRUPATE: ce a propus structura
  // (stâlpi, grinzi, pereții păstrați) deasupra, ce vine din arhitectură
  // dedesubt, cu numele fără «document». Numele întreg al
  // stratului rămâne în `title`.
  const straturiMeniu = data.layers.map((l, i) => ({ ...l, i }))
    .filter((l) => l.count > 0).map((l) => ({ ...l, ...grupaStratului(l.name) }));
  const butonStrat = (l: (typeof straturiMeniu)[number]) => (
    <button key={l.i} type="button"
            className={`meniu-comutator${visible[l.i] ? " on" : ""}`}
            aria-pressed={visible[l.i]} title={l.name}
            onClick={() => {
              const next = visible.slice();
              next[l.i] = !next[l.i];
              setVisible(next);
            }}>
      <span className="dv-swatch" style={{ background: displayColor(l.color) }} />
      <span className="meniu-comutator-nume">{l.nume}</span>
      <span className="meniu-comutator-nr">{l.count}</span>
    </button>
  );
  const inMeniu = straturiIn && createPortal(
    <>
      <div className="meniu-comutatoare meniu-lista">
        {(["structura", "arhitectura"] as const).map((g) => {
          const ale = straturiMeniu.filter((l) => l.grupa === g);
          if (!ale.length) return null;
          return (
            <div key={g} className="meniu-comutatoare">
              <span className="lbl meniu-grupa">
                {t(g === "structura" ? "struct.dwg.grupa_structura" : "struct.dwg.grupa_arhitectura")}
              </span>
              {ale.map(butonStrat)}
            </div>
          );
        })}
      </div>
      <span className="meniu-actiuni">
        <button type="button" onClick={() => setAll(true)}>{t("struct.dwg.toate")}</button>
        <button type="button" onClick={() => setAll(false)}>{t("struct.fem.lay_nimic")}</button>
      </span>
    </>,
    straturiIn);

  return (
    <div className={pageClass}>
      {header}
      {inMeniu}
      <div className="dv-body">
        {!straturiIn && <aside className="dv-layers">
          <div className="dv-layers-head">
            <input
              placeholder={t("struct.dwg.cauta_p1_layere", { p1: data.layers.length })}
              value={layerFilter}
              onChange={(e) => setLayerFilter(e.target.value)}
            />
            <div className="dv-layers-actions">
              <button onClick={() => setAll(true)}>{t("struct.dwg.toate")}</button>
              <button onClick={() => setAll(false)}>{t("struct.dwg.niciunul")}</button>
              <span className="sg-muted">{t("struct.dwg.vizibile", { n: visCount, total: data.layers.length })}</span>
            </div>
          </div>
          <div className="dv-layers-list">
            {filteredLayers.map((l) => (
              <label key={l.i} className="dv-layer">
                <input
                  type="checkbox"
                  checked={visible[l.i]}
                  onChange={(e) => {
                    const next = visible.slice();
                    next[l.i] = e.target.checked;
                    setVisible(next);
                  }}
                />
                <span className="dv-swatch" style={{ background: displayColor(l.color) }} />
                <span className="dv-layer-name" title={l.name}>{l.name}</span>
                <span className="dv-layer-count">({l.count})</span>
              </label>
            ))}
          </div>
        </aside>}

        <div className="dv-canvas-wrap" ref={wrapRef}>
          <canvas ref={canvasRef} />
          {zonaSel && (
            <div className="dv-zona-banner">
              {zonaCurent.length > 0
                ? t("struct.dwg.banner_puncte", {
                    n: tn("struct.dwg.puncte_puse#n", zonaCurent.length),
                  })
                : zonaLucru.length === 0
                ? t("struct.dwg.banner_primul")
                : t("struct.dwg.banner_zone", {
                    n: tn("struct.dwg.zone_desenate#n", zonaLucru.length),
                  })}
            </div>
          )}
          {zonaErr && <div className="dv-zona-banner dv-zona-eroare">{zonaErr}</div>}
          {selected && (
            <div className="dv-info">
              <div className="dv-info-head">
                <strong>{selected.nm || describeType(selected)}</strong>
                <button onClick={() => setSelected(null)}>×</button>
              </div>
              {selected.nm && (
                <div className="dv-info-sub sg-muted">{describeType(selected)}</div>
              )}
              <InfoRows entity={selected} data={data} />
              {(onJump ?? onSelect) && selected.id && (
                <button className="dv-info-jump"
                        onClick={() => (onJump ?? onSelect)?.(
                          selected.id ?? null, selected)}>
                  {linkEticheta ?? t("struct.dwg.vezi_in_tabel")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// nearest visible entity within tolerance (world units)
function pick(
  data: DrawingData,
  bb: Float64Array,
  vis: boolean[],
  x: number,
  y: number,
  tol: number
): DrawingEntity | null {
  let best: DrawingEntity | null = null;
  let bestD = tol;
  const ents = data.entities;
  for (let i = 0; i < ents.length; i++) {
    const e = ents[i];
    if (!vis[e.l]) continue;
    const o = i * 4;
    if (x < bb[o] - tol || x > bb[o + 2] + tol || y < bb[o + 1] - tol || y > bb[o + 3] + tol) {
      continue;
    }
    let d = Infinity;
    if (e.t === "p") {
      const rings = ringsOf(e);
      outer: for (const pts of rings) {
        for (let j = 0; j < pts.length - 1; j++) {
          d = Math.min(d, segDist(x, y, pts[j], pts[j + 1]));
          if (d < bestD * 0.25) break outer;
        }
        if (e.closed && pts.length > 2) {
          d = Math.min(d, segDist(x, y, pts[pts.length - 1], pts[0]));
        }
      }
      // filled/hatched areas are clickable anywhere inside, like AutoCAD;
      // slightly penalized so nearby linework still wins over big fills
      if (d >= bestD && e.closed && (e.fill || e.segs) && pointInRings(x, y, rings)) {
        d = tol * 0.9;
      }
    } else if (e.t === "t") {
      d = Math.hypot(x - e.x, y - e.y) - e.h;
      if (d < 0) d = 0;
    } else {
      d = Math.hypot(x - e.x, y - e.y);
    }
    if (d < bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

// even-odd point-in-polygon over all rings
function pointInRings(x: number, y: number, rings: [number, number][][]) {
  let inside = false;
  for (const pts of rings) {
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const xi = pts[i][0], yi = pts[i][1];
      const xj = pts[j][0], yj = pts[j][1];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
  }
  return inside;
}

function segDist(px: number, py: number, a: [number, number], b: [number, number]) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - a[0]) * dx + (py - a[1]) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (a[0] + t * dx), py - (a[1] + t * dy));
}

// piciorul perpendicularei pe segment (limitat la capete) + distanța până la
// el: fixarea zonei de interes pe o linie de perete, când nu e niciun vârf
// prin apropiere
function segFoot(px: number, py: number, a: [number, number], b: [number, number]) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - a[0]) * dx + (py - a[1]) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const x = a[0] + t * dx;
  const y = a[1] + t * dy;
  return { x, y, d: Math.hypot(px - x, py - y) };
}

function polyLength(pts: [number, number][], closed: boolean) {
  let len = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    len += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1]);
  }
  if (closed && pts.length > 2) {
    len += Math.hypot(pts[0][0] - pts[pts.length - 1][0], pts[0][1] - pts[pts.length - 1][1]);
  }
  return len;
}

// Numele tipurilor de entitate: CHEI, nu texte. Tipul vine din DXF cu codul
// lui (LWPOLYLINE, HATCH), iar numele citibil e al interfeței.
const TYPE_LABELS: Record<string, Cheie> = {
  LINE: "struct.dwg.tip_linie",
  LWPOLYLINE: "struct.dwg.tip_polilinie",
  POLYLINE: "struct.dwg.tip_polilinie",
  CIRCLE: "struct.dwg.tip_cerc",
  ARC: "struct.dwg.tip_arc",
  ELLIPSE: "struct.dwg.tip_elipsa",
  SPLINE: "struct.dwg.tip_spline",
  HATCH: "struct.dwg.tip_hasura",
  TEXT: "struct.dwg.tip_text",
  MTEXT: "struct.dwg.tip_text",
  ATTRIB: "struct.dwg.tip_atribut",
  SOLID: "struct.dwg.tip_solid",
  TRACE: "struct.dwg.tip_solid",
  "3DFACE": "struct.dwg.tip_fata_3d",
  OUTLINE: "struct.dwg.tip_contur_calculat",
  FOUNDATION: "struct.dwg.tip_ax_fundatie",
  FOUNDATION_VOL: "struct.dwg.tip_volum_fundatie",
  COLUMN: "struct.dwg.tip_stalp_propus",
  BEAM: "struct.dwg.tip_grinda_propusa",
  SIMPLIFIED_WALL: "struct.dwg.tip_perete_simplificat",
  PERSISTENT_WALL: "struct.dwg.tip_perete_persistent",
  RECT_ID: "struct.dwg.tip_eticheta_perete",
  PERSIST_ID: "struct.dwg.tip_eticheta_perete_persistent",
  FOUNDATION_AXIS: "struct.dwg.tip_ax_fundatie_denumit",
  AXIS_ID: "struct.dwg.tip_eticheta_ax",
};

function ringArea(pts: [number, number][]) {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += pts[j][0] * pts[i][1] - pts[i][0] * pts[j][1];
  }
  return Math.abs(a) / 2;
}

function polyArea(e: DrawingPoly) {
  let a = ringArea(e.pts);
  for (const h of e.holes ?? []) a -= ringArea(h);
  return Math.max(a, 0);
}

function fmtArea(a: number, units: string) {
  if (units === "mm") return `${(a / 1e6).toFixed(2)} m²`;
  if (units === "cm") return `${(a / 1e4).toFixed(2)} m²`;
  if (units === "m") return `${a.toFixed(2)} m²`;
  return units === "-" ? a.toFixed(1) : `${a.toFixed(1)} ${units}²`;
}

function describeType(e: DrawingEntity) {
  if (e.t === "n") return t("struct.elp.col_punct");
  const label = TYPE_LABELS[e.et] ? t(TYPE_LABELS[e.et]) : e.et;
  return `${label} (${e.et})`;
}

function InfoRows({ entity, data }: { entity: DrawingEntity; data: DrawingData }) {
  useT();
  const layer = data.layers[entity.l];
  const u = data.units === "-" ? "" : ` ${data.units}`;
  return (
    <div className="dv-info-rows">
      {entity.id && (
        <div><span>ID</span><span className="sg-mono">{entity.id}</span></div>
      )}
      <div><span>Layer</span><span title={layer?.name}>{layer?.name ?? "?"}</span></div>
      {entity.t === "p" && entity.ax && (
        <>
          <div><span>{t("struct.dwg.directie")}</span><span>{entity.ax.directie ?? "—"}</span></div>
          {entity.ax.B_m != null && (
            <div>
              <span>{t("struct.dwg.sectiune_talpa")}</span>
              <span>
                <strong>B = {entity.ax.B_m} m</strong>
                {entity.ax.H_c_m != null && ` · H_c = ${entity.ax.H_c_m} m`}
                {entity.ax.Df_m != null && ` · Df = ${entity.ax.Df_m} m`}
              </span>
            </div>
          )}
          <div>
            <span>{t("struct.dwg.lungime_ax")}</span>
            <span>{entity.ax.L_m ?? "—"} m
              {entity.ax.lungime_fundatie_m != null
                && t("struct.dwg.fundatie_p1_m", { p1: entity.ax.lungime_fundatie_m })}</span>
          </div>
          <div>
            <span>{t("struct.rzv.th_acoperire")}</span>
            <span>{entity.ax.acoperire_pct ?? "—"}%</span>
          </div>
        </>
      )}
      <div>
        <span>{t("struct.dwg.culoare")}</span>
        <span><i className="dv-swatch" style={{ background: displayColor(entity.c) }} /> {entity.c}</span>
      </div>
      {entity.t === "p" && (
        <>
          {/* elementele generate (stâlpi/grinzi/pereți): punctul din stânga
              + dimensiunile reale L × l × h */}
          {entity.info && (
            <>
              <div>
                <span>{t("struct.dwg.punct_stanga")}</span>
                <span className="sg-mono">
                  ({entity.info.p_stanga[0].toFixed(0)}, {entity.info.p_stanga[1].toFixed(0)}){u}
                </span>
              </div>
              <div><span>{t("struct.fem.lungime")}</span><span>{entity.info.lungime_m} m</span></div>
              <div><span>{t("struct.dwg.latime")}</span><span>{entity.info.latime_m} m</span></div>
              {entity.info.inaltime_m != null && (
                <div><span>{t("struct.dwg.inaltime")}</span><span>{entity.info.inaltime_m} m</span></div>
              )}
            </>
          )}
          {!entity.info && entity.pts.length >= 2 && (() => {
            // fallback generic: colțul din stânga al conturului
            const p0 = entity.pts.reduce((a, b) =>
              b[0] < a[0] || (b[0] === a[0] && b[1] < a[1]) ? b : a
            );
            return (
              <div>
                <span>{t("struct.dwg.punct_stanga")}</span>
                <span className="sg-mono">({p0[0].toFixed(0)}, {p0[1].toFixed(0)}){u}</span>
              </div>
            );
          })()}
          <div>
            <span>{entity.closed ? t("struct.dwg.contur") : t("struct.fem.lungime")}</span>
            <span>{polyLength(entity.pts, entity.closed === 1).toFixed(1)}{u}</span>
          </div>
          {entity.closed === 1 && entity.pts.length >= 3 && (
            <div><span>{t("insp.rand.arie")}</span><span>{fmtArea(polyArea(entity), data.units)}</span></div>
          )}
          {entity.pn && <div><span>Model</span><span>{entity.pn}</span></div>}
          {entity.w ? (
            <div><span>{t("insp.rand.grosime")}</span><span>{(entity.w / 100).toFixed(2)} mm</span></div>
          ) : null}
          <div><span>{t("struct.dwg.puncte")}</span><span>{entity.pts.length}{entity.closed ? t("struct.dwg.puncte_inchis") : ""}</span></div>
        </>
      )}
      {entity.t === "t" && (
        <>
          <div><span>{t("struct.dwg.continut")}</span><span className="dv-text-content">{entity.s}</span></div>
          <div><span>{t("struct.dwg.inaltime")}</span><span>{entity.h}{u}</span></div>
        </>
      )}
      {entity.t === "n" && (
        <div><span>{t("struct.dwg.pozitie")}</span><span>{entity.x.toFixed(1)}, {entity.y.toFixed(1)}{u}</span></div>
      )}
    </div>
  );
}
